import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronUp, FaPlus, FaGripVertical, FaSitemap, FaTimes } from 'react-icons/fa';
import { getIdeaHierarchy, updateIdeaParent, createIdea } from '../../api/ideas';
import EmojiPicker from 'emoji-picker-react';

// Recursive tree node component
function HierarchyNode({ node, currentIdeaId, level = 0, onAddChild, onMoveIdea, draggedItem, setDraggedItem, refreshHierarchy, isLastChild = false }) {
  const [showAddButton, setShowAddButton] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const navigate = useNavigate();
  const hasChildren = node.children && node.children.length > 0;
  const isCurrentIdea = node.id === currentIdeaId;

  const handleDragStart = (e) => {
    e.stopPropagation();
    setDraggedItem(node);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedItem && draggedItem.id !== node.id) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (draggedItem && draggedItem.id !== node.id) {
      // Don't allow dropping a parent onto its own child
      const isDescendant = (parent, childId) => {
        if (parent.id === childId) return true;
        if (parent.children) {
          return parent.children.some(child => isDescendant(child, childId));
        }
        return false;
      };

      if (!isDescendant(draggedItem, node.id)) {
        await onMoveIdea(draggedItem.id, node.id);
        setDraggedItem(null);
        refreshHierarchy();
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setIsDragOver(false);
  };

  return (
    <div className="select-none relative">
      {/* Tree lines */}
      {level > 0 && (
        <>
          {/* Horizontal line */}
          <div 
            className="absolute top-[18px] border-t border-[var(--border-color)]"
            style={{ 
              left: `${8 + (level - 1) * 20}px`,
              width: '12px'
            }}
          />
          {/* Vertical line */}
          {!isLastChild && (
            <div 
              className="absolute top-0 bottom-0 border-l border-[var(--border-color)]"
              style={{ 
                left: `${8 + (level - 1) * 20}px`
              }}
            />
          )}
          {isLastChild && (
            <div 
              className="absolute top-0 border-l border-[var(--border-color)]"
              style={{ 
                left: `${8 + (level - 1) * 20}px`,
                height: '18px'
              }}
            />
          )}
        </>
      )}
      
      <div
        className={`flex items-center py-1.5 px-2 rounded-lg cursor-pointer transition-all duration-200 group
          ${isCurrentIdea ? 'bg-[var(--primary)]/20' : 'hover:bg-[var(--bg-hover)]'}
          ${isDragOver ? 'bg-[var(--primary)]/30 ring-2 ring-[var(--primary)]' : ''}
        `}
        style={{ paddingLeft: `${8 + level * 20}px` }}
        onMouseEnter={() => setShowAddButton(true)}
        onMouseLeave={() => setShowAddButton(false)}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
      >

        {/* Idea title */}
        <div 
          className="flex-1 flex items-center ml-1 truncate"
          onClick={() => navigate(`/idea/${node.id}`)}
        >
          <span className={`text-sm truncate ${isCurrentIdea ? 'font-semibold text-[var(--primary)]' : 'text-[var(--text-primary)]'}`}>
            {node.title}
          </span>
        </div>

        {/* Add child button */}
        {showAddButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddChild(node.id);
            }}
            className="p-1 hover:bg-[var(--bg-secondary)] rounded-full transition-all opacity-0 group-hover:opacity-100"
            title="Add sub-idea"
          >
            <FaPlus className="w-2.5 h-2.5 text-[var(--text-secondary)] hover:text-[var(--primary)]" />
          </button>
        )}

        {/* Drag handle */}
        <FaGripVertical 
          className="w-3 h-3 ml-1 text-[var(--text-secondary)] opacity-0 group-hover:opacity-100 cursor-grab transition-opacity"
        />
      </div>

      {/* Children */}
      {hasChildren && (
        <div>
          {node.children.map((child, index) => (
            <HierarchyNode
              key={child.id}
              node={child}
              currentIdeaId={currentIdeaId}
              level={level + 1}
              onAddChild={onAddChild}
              onMoveIdea={onMoveIdea}
              draggedItem={draggedItem}
              setDraggedItem={setDraggedItem}
              refreshHierarchy={refreshHierarchy}
              isLastChild={index === node.children.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectHierarchy({ ideaId, token, refreshSidebar, isCollapsed: controlledCollapsed, onCollapseChange }) {
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [showNewSubIdeaModal, setShowNewSubIdeaModal] = useState(false);
  const [newSubIdeaParentId, setNewSubIdeaParentId] = useState(null);
  const [newSubIdeaTitle, setNewSubIdeaTitle] = useState('');
  const [newSubIdeaIcon, setNewSubIdeaIcon] = useState('💡');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const navigate = useNavigate();
  
  // Support both controlled and uncontrolled modes
  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
  
  const handleToggleCollapse = () => {
    if (onCollapseChange) {
      onCollapseChange(!isCollapsed);
    } else {
      setInternalCollapsed(!isCollapsed);
    }
  };

  const fetchHierarchy = async () => {
    if (!ideaId || !token) return;
    
    try {
      setLoading(true);
      const data = await getIdeaHierarchy(ideaId, token);
      setHierarchy(data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch hierarchy:', err);
      setError('Failed to load project structure');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHierarchy();
  }, [ideaId, token]);

  const handleAddChild = (parentId) => {
    setNewSubIdeaParentId(parentId);
    setNewSubIdeaTitle('');
    setNewSubIdeaIcon('💡');
    setShowNewSubIdeaModal(true);
  };

  const handleCreateNewSubIdea = async () => {
    if (!newSubIdeaTitle.trim()) {
      return;
    }

    try {
      const newIdea = await createIdea({
        title: newSubIdeaTitle,
        description: '',
        icon: newSubIdeaIcon,
        status: 'draft',
        is_public: false,
        tags: [],
        parent_id: newSubIdeaParentId,
        collaborator_ids: []
      }, token);
      
      setShowNewSubIdeaModal(false);
      setNewSubIdeaTitle('');
      setNewSubIdeaIcon('💡');
      refreshSidebar?.();
      await fetchHierarchy();
      navigate(`/idea/${newIdea.id}`);
    } catch (err) {
      console.error('Failed to create child idea:', err);
      setError('Failed to create new sub-idea');
    }
  };

  const handleMoveIdea = async (ideaId, newParentId) => {
    try {
      await updateIdeaParent(ideaId, newParentId, token);
      refreshSidebar?.();
      await fetchHierarchy();
    } catch (err) {
      console.error('Failed to move idea:', err);
      setError('Failed to move sub-idea');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col bg-[var(--bg-secondary)] rounded-lg shadow-sm h-full">
        <div className="flex items-center justify-between bg-[var(--bg-secondary)] rounded-t-lg p-4 border-b border-[var(--border-color)]">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[var(--bg-secondary)] rounded-lg">
              <FaSitemap size={16} className="text-[var(--primary)]" />
            </div>
            <h3 className="font-semibold text-[var(--text-primary)]">Project Structure</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-[var(--bg-hover)] rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-[var(--bg-hover)] rounded w-1/2 mb-1"></div>
            <div className="h-3 bg-[var(--bg-hover)] rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col bg-[var(--bg-secondary)] rounded-lg shadow-sm h-full">
        <div className="flex items-center justify-between bg-[var(--bg-secondary)] rounded-t-lg p-4 border-b border-[var(--border-color)]">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[var(--bg-secondary)] rounded-lg">
              <FaSitemap size={16} className="text-[var(--primary)]" />
            </div>
            <h3 className="font-semibold text-[var(--text-primary)]">Project Structure</h3>
          </div>
        </div>
        <div className="p-4">
          <p className="text-sm text-danger">{error}</p>
        </div>
      </div>
    );
  }

  if (!hierarchy) {
    return null;
  }

  return (
    <>
      {/* New Sub-Idea Modal - Rendered at document root */}
      {showNewSubIdeaModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={() => setShowNewSubIdeaModal(false)}>
          <div className="bg-[var(--bg-secondary)] rounded-lg shadow-xl p-6 w-96 max-w-[90%]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Create New Sub-Idea</h3>
              <button
                onClick={() => setShowNewSubIdeaModal(false)}
                className="p-1 hover:bg-[var(--bg-hover)] rounded transition-colors"
              >
                <FaTimes className="text-[var(--text-secondary)]" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Icon Picker */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">Icon</label>
                <div className="relative">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="text-4xl p-2 hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
                  >
                    {newSubIdeaIcon}
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute top-full left-0 mt-2 z-50">
                      <EmojiPicker
                        onEmojiClick={(emojiObject) => {
                          setNewSubIdeaIcon(emojiObject.emoji);
                          setShowEmojiPicker(false);
                        }}
                        theme="dark"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Title Input */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">Sub-Idea Name</label>
                <input
                  type="text"
                  value={newSubIdeaTitle}
                  onChange={(e) => setNewSubIdeaTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newSubIdeaTitle.trim()) {
                      handleCreateNewSubIdea();
                    }
                  }}
                  placeholder="Enter sub-idea name..."
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  autoFocus
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowNewSubIdeaModal(false)}
                className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewSubIdea}
                disabled={!newSubIdeaTitle.trim()}
                className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="flex flex-col bg-[var(--bg-secondary)] rounded-lg shadow-sm transition-all duration-200 ease-out w-full h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between bg-[var(--bg-secondary)] rounded-t-lg p-4 border-b border-[var(--border-color)]">
        <div className="flex items-center space-x-3">
          {!isCollapsed && (
            <div className="p-2 bg-[var(--bg-secondary)] rounded-lg">
              <FaSitemap size={16} className="text-[var(--primary)]" />
            </div>
          )}
          <h3 className={`font-semibold text-[var(--text-primary)] ${isCollapsed ? 'text-sm' : ''}`}>Project Structure</h3>
        </div>
        <button
          onClick={handleToggleCollapse}
          className="p-2 hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:scale-110"
        >
          {isCollapsed ? (
            <FaChevronDown size={16} className="transform transition-transform duration-300" />
          ) : (
            <FaChevronUp size={16} className="transform transition-transform duration-300" />
          )}
        </button>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-transparent">
          <HierarchyNode
            node={hierarchy.root}
            currentIdeaId={hierarchy.current_idea_id}
            level={0}
            onAddChild={handleAddChild}
            onMoveIdea={handleMoveIdea}
            draggedItem={draggedItem}
            setDraggedItem={setDraggedItem}
            refreshHierarchy={fetchHierarchy}
          />
        </div>
      )}
    </div>
    </>
  );
}

export default ProjectHierarchy;
